import { useState } from "react"
import { CheckCircle, X, AlertCircle, Info, XCircle } from "lucide-react"
import { Button } from "../ui/button"

export default function EditorFooter({
  saveStatus,
  lastSaved,
  tips,
  notifications,
  onRemoveTip,
  onRemoveNotification
}) {
  const [expanded, setExpanded] = useState(false)

  const formatTime = (date) => {
    if (!date) return ''
    return new Date(date).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  const getStatusIcon = () => {
    switch (saveStatus) {
      case 'saving':
        return <div className="w-2 h-2 bg-[#203a8a] rounded-full animate-pulse" />
      case 'saved':
        return <CheckCircle className="w-4 h-4 text-[#203a8a]" />
      case 'error':
        return <XCircle className="w-4 h-4 text-gray-700" />
      default:
        return <div className="w-2 h-2 bg-gray-400 rounded-full" />
    }
  }

  const getStatusText = () => {
    switch (saveStatus) {
      case 'saving':
        return 'Sauvegarde en cours...'
      case 'saved':
        return lastSaved ? `Sauvegardé à ${formatTime(lastSaved)}` : 'Sauvegardé'
      case 'error':
        return 'Erreur de sauvegarde'
      default:
        return 'Non sauvegardé'
    }
  }

  return (
    <footer className="h-16 bg-white border-t border-gray-200 flex items-center justify-between px-6 shadow-sm z-10">
      {/* Left: Status */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className="text-sm text-gray-600">{getStatusText()}</span>
        </div>
      </div>

      {/* Center: Tips */}
      {tips.length > 0 && (
        <div className="flex-1 flex items-center gap-2 px-4">
          {tips.slice(0, 2).map((tip, index) => (
            <div
              key={index}
              className="flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800"
            >
              <Info className="w-4 h-4" />
              <span>{tip}</span>
              <button
                onClick={() => onRemoveTip(index)}
                className="ml-2 hover:bg-blue-100 rounded p-1"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {tips.length > 2 && (
            <span className="text-xs text-gray-500">+{tips.length - 2} autre(s)</span>
          )}
        </div>
      )}

      {/* Right: Notifications */}
      <div className="flex items-center gap-2">
        {notifications.length > 0 && (
          <div className="flex items-center gap-2">
            {notifications.slice(0, 3).map((notification) => (
              <div
                key={notification.id}
                className={`flex items-center gap-2 px-3 py-1 rounded-lg text-sm ${
                  notification.type === 'success'
                    ? 'bg-blue-50 text-[#203a8a] border border-blue-200'
                    : notification.type === 'error'
                    ? 'bg-gray-100 text-gray-800 border border-gray-300'
                    : 'bg-blue-50 text-[#203a8a] border border-blue-200'
                }`}
              >
                {notification.type === 'success' && <CheckCircle className="w-4 h-4" />}
                {notification.type === 'error' && <XCircle className="w-4 h-4" />}
                {notification.type === 'info' && <Info className="w-4 h-4" />}
                <span>{notification.message}</span>
                <button
                  onClick={() => onRemoveNotification(notification.id)}
                  className="ml-2 hover:opacity-70"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            {notifications.length > 3 && (
              <span className="text-xs text-gray-500">+{notifications.length - 3}</span>
            )}
          </div>
        )}
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded(!expanded)}
          className="h-8"
        >
          {expanded ? 'Réduire' : 'Détails'}
        </Button>
      </div>

      {/* Expanded View */}
      {expanded && (
        <div className="absolute bottom-16 left-0 right-0 bg-white border-t border-gray-200 shadow-lg p-4 max-h-64 overflow-y-auto">
          <div className="space-y-4">
            {/* All Tips */}
            {tips.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Conseils
                </h4>
                <div className="space-y-2">
                  {tips.map((tip, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-blue-50 rounded-lg"
                    >
                      <span className="text-sm text-blue-800">{tip}</span>
                      <button
                        onClick={() => onRemoveTip(index)}
                        className="ml-2 hover:bg-blue-100 rounded p-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* All Notifications */}
            {notifications.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Notifications
                </h4>
                <div className="space-y-2">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`flex items-center justify-between p-2 rounded-lg ${
                        notification.type === 'success'
                          ? 'bg-blue-50'
                          : notification.type === 'error'
                          ? 'bg-gray-100'
                          : 'bg-blue-50'
                      }`}
                    >
                      <span className={`text-sm ${
                        notification.type === 'success'
                          ? 'text-[#203a8a]'
                          : notification.type === 'error'
                          ? 'text-gray-800'
                          : 'text-[#203a8a]'
                      }`}>
                        {notification.message}
                      </span>
                      <button
                        onClick={() => onRemoveNotification(notification.id)}
                        className="ml-2 hover:opacity-70"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tips.length === 0 && notifications.length === 0 && (
              <div className="text-center text-gray-500 py-4">
                <p className="text-sm">Aucun conseil ou notification</p>
              </div>
            )}
          </div>
        </div>
      )}
    </footer>
  )
}

